<template>
	<span>
		<b-button
			v-b-modal="'promptgen-' + id"
			:variant="buttonVariant"
			:title="title"
			@click="openModal">
			<i :class="buttonIcon"></i>
			<span v-if="buttonLabel" class="ml-1">{{ buttonLabel }}</span>
		</b-button>

		<b-modal
			size="xl"
			:id="'promptgen-' + id"
			:title="modalTitle"
			hide-footer
		>
			<p>
				Prompt
				<b-form-textarea
					v-model="prompt"
					rows="5"
					max-rows="12"
					:placeholder="promptPlaceholder"
				/>
			</p>

			<slot name="options"></slot>

			<div v-if="stage === 'review'">
				<p v-if="validation_errors.length > 0" style="color: #b00020">
					<b>Erreurs de validation</b>
					<ul>
						<li v-for="(err, idx) in validation_errors" :key="idx">{{ err }}</li>
					</ul>
				</p>
				<b>Résultat généré</b>
				<b-form-textarea :value="lastResultDisplay" rows="12" readonly style="font-family: monospace; font-size: 12px;" />
				<b-row class="mt-3">
					<b-col class="text-center">
						<b-button
							variant="success"
							:disabled="!lastResultRaw || validation_errors.length > 0"
							@click="applyResult">
							<i class="fas fa-check"></i> Appliquer
						</b-button>
					</b-col>
				</b-row>
			</div>

			<p v-if="error" style="color: #b00020; white-space: pre-wrap">{{ error }}</p>

			<div class="d-flex justify-content-between align-items-center mt-3">
				<div>
					<b-button variant="secondary" @click="$bvModal.hide('promptgen-' + id)">Fermer</b-button>
				</div>
				<div>
					<b-button variant="primary" :disabled="loading || !prompt" @click="generate">
						<span v-if="!loading">{{ lastResultRaw ? 'Régénérer' : 'Générer' }}</span>
						<span v-else>Génération…</span>
					</b-button>
				</div>
			</div>
		</b-modal>
	</span>
</template>

<script>
import uniqueId from "lodash.uniqueid";
import workers from "../services/workers.js";
import treatments from "../services/treatments.js";
import * as json2csv from "json2csv";

export default {
	name: "PromptGenerator",
	props: {
		buildRequest: {
			type: Function,
			required: true
		},
		parseResult: {
			type: Function,
			default: null
		},
		validateResult: {
			type: Function,
			default: null
		},
		config: {
			type: String,
			default: "documentation scenario"
		},
		model: {
			type: String,
			default: null
		},
		title: {
			type: String,
			default: "Générer avec l'IA"
		},
		modalTitle: {
			type: String,
			default: "Génération par IA"
		},
		promptPlaceholder: {
			type: String,
			default: "Décrivez ce que vous voulez générer..."
		},
		buttonVariant: {
			type: String,
			default: "link"
		},
		buttonIcon: {
			type: String,
			default: "fas fa-magic"
		},
		buttonLabel: {
			type: String,
			default: null
		},
		// Schéma JSON (OpenAI Structured Outputs) pour imposer un format de réponse strict
		jsonSchema: {
			type: Object,
			default: null
		}
	},
	data() {
		return {
			id: uniqueId(""),
			stage: "prompt",
			prompt: "",
			loading: false,
			error: "",
			validation_errors: [],
			lastResultRaw: "",
			lastResultParsed: null
		};
	},
	computed: {
		lastResultDisplay() {
			if (!this.lastResultRaw) return '';
			try {
				return JSON.stringify(JSON.parse(this.lastResultRaw), null, 2);
			} catch (e) {
				return this.lastResultRaw;
			}
		}
	},
	methods: {
		openModal() {
			this.stage = this.lastResultRaw ? "review" : "prompt";
		},
		applyResult() {
			if (this.validation_errors.length > 0)
				return;

			this.$emit("result", this.lastResultParsed);

			this.$bvModal.hide("promptgen-" + this.id);

			this.$notify({
				group: "info",
				text: "Résultat appliqué avec succès",
				type: "success",
				duration: 3000
			});
		},
		sanitizeResult(str) {
			if (!str) return "";
			var s = String(str).trim();

			// Retire les fences markdown si présentes
			if (s.startsWith("```")) {
				s = s.replace(/^```[a-zA-Z]*\n?/, "");
				s = s.replace(/```\s*$/, "");
			}

			return s.trim();
		},
		async generate() {
			this.error = "";
			this.validation_errors = [];
			this.lastResultRaw = "";
			this.lastResultParsed = null;

			if (!this.prompt)
				return;

			this.loading = true;
			try {
				var request = this.buildRequest(this.prompt);

				var params = {
					config: this.config,
					template: false,
					content: "request",
					column_result: "result",
					column_error: "error"
				};
				if (this.model)
					params.model = this.model;
				if (this.jsonSchema)
					params.json_schema = this.jsonSchema;

				var formData = new FormData();
				formData.append("action", "saas_back-openai");
				formData.append("parameters", JSON.stringify(params));

				var data = [{ request: request }];
				var str_csv = json2csv.parse(data, {
					fields: Object.keys(data[0]),
					fieldNames: Object.keys(data[0])
				});

				var files = [
					new File([str_csv], "data.csv", { type: "text/csv" })
				];
				formData.append(files[0].name, files[0], "data.csv");

				var result = await workers.test(formData);
				var csv_data = await treatments.getFileAlyx(
					result.data.files[0].url,
					false,
					false
				);
				var parsed = this.$papa.parse(csv_data, { header: true });
				var rows = parsed && parsed.data ? parsed.data : [];
				var raw = rows && rows[0] ? (rows[0].result || "") : "";
				var sanitized = this.sanitizeResult(raw);

				this.lastResultRaw = sanitized;

				if (this.parseResult) {
					try {
						this.lastResultParsed = this.parseResult(sanitized);
					} catch (e) {
						this.validation_errors.push("Erreur lors de l'analyse du résultat : " + (e && e.message ? e.message : String(e)));
						this.lastResultParsed = sanitized;
					}
				} else {
					this.lastResultParsed = sanitized;
				}

				if (this.validateResult && this.validation_errors.length === 0) {
					this.validation_errors = this.validateResult(this.lastResultParsed);
				}

				this.stage = "review";
			} catch (e) {
				this.error = e && e.message ? e.message : String(e);
			} finally {
				this.loading = false;
			}
		}
	}
};
</script>

<style scoped>
</style>
